import { Component } from '@theme/component';

/**
 * `<bundle-step>` — presentation host for one bundle step.
 *
 * Pure presentation: shows/hides itself based on the builder's current step
 * and reflects the active group's tab pressed-state, swatch group visibility,
 * derived "+$x" tier deltas, and the selected product name/image. It mutates
 * nothing directly — child controls use declarative `on:` bindings against the
 * `<bundle-builder>` contract.
 *
 * Owned by the scaffold. Phase 1 (Alex) may extend the render logic here but
 * must keep the `data-step`, `ref`, and `.bb-group-tab`/`.bb-swatches`
 * selectors stable, since the snippet + downstream components depend on them.
 */
export class BundleStepComponent extends Component {
  #builder = null;

  connectedCallback() {
    super.connectedCallback();
    this.stepIndex = Number(this.dataset.step);
    // The snippet server-rendered the gallery + accordions for this product
    // (`data-product-id`). Seed the render keys with it so the first
    // bundle:update — which resolves to the same product — doesn't replace
    // identical markup and re-request the already-painted LCP image. The zoom
    // dialog is JS-only, so it is still built on that first pass.
    const ssrId = this.dataset.productId || null;
    this.#galleryKey = ssrId;
    this.#accordionKey = ssrId;
    this.#builder = this.closest('bundle-builder');
    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
    this.#builder?.requestUpdate?.();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
  }

  /** productId whose gallery is currently rendered, to skip redundant rebuilds. */
  #galleryKey = null;

  #onUpdate = (event) => {
    const builder = this.#builder;
    if (!builder) return;
    const { currentStep } = event.detail;
    this.toggleAttribute('hidden', currentStep !== this.stepIndex);

    // The head's price is the WHOLE bundle's, so it renders ahead of the
    // selection guard below: the last (accessory) step deliberately holds a null
    // draft product until the shopper picks one, and the bundle is still priced
    // meanwhile.
    this.#renderPrice(event.detail.totals ?? builder.totals);

    // Reflect the DRAFT (in-progress) pick — the committed bundle only changes
    // on Save, but the step itself must show what the shopper is choosing.
    const sel = builder.getDraftSelection(this.stepIndex);
    if (!sel) return;
    const activeGroup = sel.state.groupIndex;

    // Reflect active group: tab pressed-state + swatch / dropdown visibility.
    // Scoped to the tab ROW: `.bb-group-tab` is also the pill treatment worn by
    // the variant and size buttons, and a size pill carries a `data-group` (its
    // counterpart's tier) that this loop would read as a tier selection.
    for (const tab of this.querySelectorAll('.bb-group-tabs .bb-group-tab')) {
      tab.setAttribute('aria-pressed', String(Number(tab.dataset.group) === activeGroup));
    }
    for (const swatches of this.querySelectorAll('.bb-swatches')) {
      swatches.toggleAttribute('hidden', Number(swatches.dataset.group) !== activeGroup);
    }
    // Accessory type dropdown: name the active group on the trigger and drop it
    // from the list (CSS hides the aria-selected row), then collapse. Only a
    // state change reaches here, so opening the list never closes it.
    for (const option of this.querySelectorAll('.bb-select__option')) {
      option.setAttribute('aria-selected', String(Number(option.dataset.group) === activeGroup));
    }
    if (this.refs.typeLabel && sel.group?.label) this.refs.typeLabel.textContent = sel.group.label;
    if (this.refs.typeSelect instanceof HTMLDetailsElement) this.refs.typeSelect.open = false;

    // Type deltas, against the step's current pick rather than a fixed baseline,
    // so they re-baseline as the shopper changes their mind. Signed: a group
    // cheaper than what is selected reads as a saving. The ACTIVE group never
    // carries one — it is the thing being compared against, and a shopper who
    // picked a cheaper colourway within it would otherwise see their own tier
    // quote a difference from itself. Idempotent.
    //
    // Only the ACCESSORY step's dropdown rows still carry these: on the tab row
    // a per-tier difference read as pricing rather than as a choice, so the tabs
    // gave theirs up (see the snippet) and the shopper's own pick states its
    // difference on the colour line instead. The dropdown keeps its column
    // because a closed list has to justify what opening it would cost.
    for (const el of this.refs.groupDelta ?? []) {
      const groupIndex = Number(el.dataset.group);
      const delta = groupIndex === activeGroup ? 0 : builder.groupDelta(this.stepIndex, groupIndex);
      el.textContent = this.#formatDelta(builder, delta);
    }

    // The chosen item's own delta, at the end of the colour line. Baselined on
    // the bundle as merchandised, not on the current pick — see
    // `selectionDelta`. Runs unconditionally so it clears itself on a step with
    // nothing chosen yet (the accessory step before a pick).
    if (this.refs.selectedDelta) {
      this.refs.selectedDelta.textContent = this.#formatDelta(builder, builder.selectionDelta?.(this.stepIndex) ?? 0);
    }

    // The title names what is picked WITHIN the step; the eyebrow above it
    // already names the step ("Select Your Ryder Bag"). It is ALWAYS the product
    // name's prefix — the part before " | " — and never the group label, so a
    // bag is named the same here as everywhere else on the site: "Ryder 14", not
    // the tier it happens to be merchandised under ("Ryder Classic"). Same
    // derivation (and same `strip`) as snippets/linked-product-swatches.liquid,
    // which feeds `size-way-label`.
    //
    // This is why the step no longer carries `data-dynamic-title`: it used to
    // pick between this and the group label, and both branches now agree. The
    // group label survives only as the stand-in before a product resolves.
    if (this.refs.title) {
      const titleText = sel.product?.title?.split(' | ')[0].trim() || sel.group?.label;
      if (titleText) this.refs.title.textContent = titleText;
    }

    // Last step (accessory): the CTA stays "Make a Selection" (disabled) until
    // an accessory is actually chosen. It then reads "Checkout", matching where
    // `finish` sends the shopper once every step is committed — the section's
    // "On bundle complete" setting decides that, so keep the two in step if it
    // is ever moved off checkout.
    if (this.hasAttribute('data-is-last') && this.refs.primaryBtn instanceof HTMLButtonElement) {
      const chosen = !!sel.product;
      this.refs.primaryBtn.disabled = !chosen;
      this.refs.primaryBtn.textContent = chosen ? builder.ctaLabel ?? 'Checkout' : 'Make a Selection';
    }

    // Both pickers run on every update (even with no product, so they clear).
    this.#renderSizes(builder);
    this.#renderVariants(sel);

    if (sel.product) {
      if (this.refs.selectedName) this.refs.selectedName.textContent = sel.product.color || sel.product.title;
      this.#renderGallery(sel.product);
      this.#syncAccordions(sel.product);
    }
  };

  /**
   * The bundle's running price in the head's right column.
   *
   * Read off the event's `totals` — the object the sticky bottom bar renders
   * from — rather than re-derived here, so the two figures on screen can never
   * disagree. Lives on the STEP rather than on <bundle-reviews> (which used to
   * carry it under the stars) because it prices the bundle, not the reviewed
   * product, and the step owns the head layout it now has a column in.
   *
   * @param {{subtotal:number, total:number, savings:number} | undefined} totals
   */
  #renderPrice(totals) {
    const { pricing, priceCompare, priceTotal } = this.refs;
    if (!(pricing instanceof HTMLElement)) return;
    // money() is defined on BundleBuilderComponent, not on Element. Without it
    // (or without totals) leave the block hidden rather than print raw cents.
    if (!totals || typeof this.#builder?.money !== 'function') return;

    // Nothing priced yet (no steps, or no purchasable option) — a "$0.00" beside
    // the title reads as a broken bundle, so show no price at all.
    pricing.toggleAttribute('hidden', totals.total <= 0);

    // Same rule as the bar: without a discount the compare-at would just repeat
    // the price struck through, so drop the line entirely.
    const hasSavings = totals.savings > 0;
    if (priceCompare instanceof HTMLElement) {
      priceCompare.textContent = hasSavings ? this.#builder.money(totals.subtotal) : '';
      priceCompare.toggleAttribute('hidden', !hasSavings);
    }

    if (priceTotal instanceof HTMLElement) priceTotal.textContent = this.#builder.money(totals.total);
  }

  /**
   * A signed price difference as the shopper reads it: "+$30", "−$50", or
   * nothing at all when there is no difference to state. Whole dollars — every
   * price in the catalogue ends in .99, so the cents are noise on a difference.
   * @param {any} builder @param {number} cents
   */
  #formatDelta(builder, cents) {
    if (!cents) return '';
    return `${cents > 0 ? '+' : '−'}${builder.money(Math.abs(cents)).replace('.00', '')}`;
  }

  /** The option set (as "group:product|…") whose size pills are rendered. */
  #sizeKey = null;

  /**
   * Rebuild the size row (5-Way / 14-Way) for the selected colourway.
   *
   * Unlike the variant row below, the options are not a property of the
   * selected product — each divider count is a separate product, found across
   * the step's groups by the builder (`sizeOptions`). So the key is the option
   * SET rather than a productId: switching between two counterparts leaves the
   * same three pills on screen and only moves which one is pressed, while
   * switching colourway replaces them (or empties the row, which is the case
   * for 30 of the Ryder bundle's 33 colourways).
   *
   * @param {any} builder
   */
  #renderSizes(builder) {
    const row = this.refs.sizeRow;
    if (!(row instanceof HTMLElement)) return;

    // Empty with nothing selected, and empty for a builder predating the API —
    // either way the row hides rather than keeping a stale set of pills.
    const options = builder.sizeOptions?.(this.stepIndex) ?? [];
    const key = options.map((option) => `${option.groupIndex}:${option.productId}`).join('|');
    if (this.#sizeKey !== key) {
      this.#sizeKey = key;
      const list = row.querySelector('.bb-sizes__row');
      if (list) list.innerHTML = options.map((option) => this.#sizeButton(option)).join('');
    }

    row.toggleAttribute('hidden', options.length < 2);
    if (options.length < 2) return;

    const active = options.find((option) => option.active);
    for (const button of row.querySelectorAll('.bb-size')) {
      button.setAttribute('aria-pressed', String(button.dataset.productId === String(active?.productId ?? '')));
    }
    const value = row.querySelector('.bb-sizes__value');
    if (value) value.textContent = active?.label ?? '';
  }

  /**
   * One size pill. Carries the counterpart's `data-group` as well as its
   * `data-product-id` because the two move together — the counterpart normally
   * lives in another tier group, and `selectSize` refuses a pill that doesn't
   * name a product really in that group.
   */
  #sizeButton(option) {
    return (
      `<button type="button" class="bb-group-tab bb-size" aria-pressed="false"` +
      ` data-step="${this.stepIndex}" data-group="${this.#esc(option.groupIndex)}"` +
      ` data-product-id="${this.#esc(option.productId)}"` +
      ` on:click="bundle-builder/selectSize?step=${this.stepIndex}">` +
      `<span class="bb-group-tab__label">${this.#esc(option.label)}</span>` +
      `</button>`
    );
  }

  /** productId whose variant buttons are currently rendered. */
  #variantKey = null;

  /**
   * Rebuild the variant picker (e.g. "Size") for the selected product.
   *
   * The option set belongs to the PRODUCT, so it changes every time the shopper
   * clicks a different colour swatch — keyed on productId like #renderGallery /
   * #syncAccordions so repeated updates are cheap and idempotent. Products with
   * a single variant (hats, towels, most accessories) hide the row entirely and
   * behave exactly as they did before the picker existed.
   *
   * @param {{product:any, variant:any}} sel
   */
  #renderVariants(sel) {
    const row = this.refs.variantRow;
    if (!(row instanceof HTMLElement)) return;

    const product = sel.product ?? null;
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const key = product ? String(product.productId) : '';

    if (this.#variantKey !== key) {
      this.#variantKey = key;
      const list = row.querySelector('.bb-variants__row');
      // Only a real choice is worth rendering — one variant is not a choice.
      if (list) list.innerHTML = variants.length > 1 ? variants.map((v) => this.#variantButton(v)).join('') : '';
      const option = row.querySelector('.bb-variants__option');
      if (option) option.textContent = `${this.#optionName(product)}:`;
    }

    row.toggleAttribute('hidden', variants.length < 2);
    if (variants.length < 2) return;

    // Reflect the resolved variant. Runs on every update (not just rebuilds) so
    // a variant click repaints the pressed state without touching the DOM tree.
    const activeId = String(sel.variant?.id ?? '');
    for (const button of row.querySelectorAll('.bb-variant')) {
      button.setAttribute('aria-pressed', String(button.dataset.variantId === activeId));
    }
    const value = row.querySelector('.bb-variants__value');
    if (value) value.textContent = this.#variantLabel(sel.variant);
  }

  /** Option name(s) of a product, e.g. "Size" — the picker's label. */
  #optionName(product) {
    const names = Array.isArray(product?.optionNames) ? product.optionNames.filter(Boolean) : [];
    return names.length ? names.join(' / ') : 'Options';
  }

  /** A variant's option VALUES ("9-12 years old 31” (H) 8” (W)"), not its title. */
  #variantLabel(variant) {
    const options = Array.isArray(variant?.options) ? variant.options.filter(Boolean) : [];
    return options.length ? options.join(' / ') : variant?.title ?? '';
  }

  /**
   * One variant pill. Carries `bb-group-tab` so it is visually identical to the
   * bag-type buttons, and the same `on:click` binding the server renders — the
   * builder reads the id off `data-variant-id`, so rebuilt buttons keep working
   * (the theme delegates `on:` handlers at the document level).
   */
  #variantButton(variant) {
    const soldOut = variant.available === false;
    return (
      `<button type="button" class="bb-group-tab bb-variant" aria-pressed="false"` +
      ` data-step="${this.stepIndex}" data-variant-id="${this.#esc(variant.id)}" data-price="${this.#esc(variant.price)}"` +
      `${soldOut ? ' disabled aria-disabled="true"' : ''}` +
      ` on:click="bundle-builder/selectVariant?step=${this.stepIndex}">` +
      `<span class="bb-group-tab__label">${this.#esc(this.#variantLabel(variant))}</span>` +
      `${soldOut ? '<span class="bb-variant__flag">Sold out</span>' : ''}` +
      `</button>`
    );
  }

  /** productId whose accordion bodies are currently rendered. */
  #accordionKey = null;

  /**
   * Swap the four accordion bodies (Product Description, Features, Specs &
   * Dimensions, Why Buy) to the selected product's content. The HTML for each
   * is rendered server-side from the product's `custom.*` rich-text metafields
   * and carried inline on the config (`detail`/`features`/`specs`/`whyBuy`) —
   * the `/products/{handle}.js` endpoint can't return metafields, so it isn't
   * used. Accordions with no content for the current product are hidden.
   */
  #syncAccordions(product) {
    if (String(this.#accordionKey) === String(product.productId)) return;
    this.#accordionKey = String(product.productId);

    const map = [
      [this.refs.descBody, product.detail],
      [this.refs.featuresBody, product.features],
      [this.refs.specsBody, product.specs],
      [this.refs.whyBody, product.whyBuy],
    ];
    for (const [body, html] of map) {
      if (!body || !(body instanceof HTMLElement)) continue;
      const value = html ?? '';
      // Only swap when the config actually carries content — never blank out
      // the server-rendered fallback for a field the config didn't include.
      if (value) body.innerHTML = value;
      const details = body.closest('details');
      if (details) details.toggleAttribute('hidden', value.trim() === '');
    }
  }

  /** Rebuild the image grid for the selected product (all images, 2-col desktop). */
  #renderGallery(product) {
    const grid = this.refs.galleryGrid;
    if (!grid || !(grid instanceof HTMLElement)) return;
    const images = (Array.isArray(product.images) && product.images.length ? product.images : [product.image]).filter(Boolean);
    if (String(this.#galleryKey) === String(product.productId)) {
      // The grid already shows this product (server-rendered, or a previous
      // pass). Only make sure the JS-built zoom dialog exists for it.
      if (!this.refs.zoomHost?.firstElementChild) this.#renderZoom(product, images);
      return;
    }
    this.#galleryKey = String(product.productId);

    const single = images.length === 1;
    const zoomId = this.#zoomId;
    // Only the step ON SCREEN may mark anything eager, and only its first image.
    //
    // `hidden` is already up to date here — `#onUpdate` sets it before it reaches
    // this — so this asks the real question: is this gallery visible at all? Every
    // step rebuilds its gallery on the first `bundle:update`, so marking `i < 2`
    // eager regardless was fetching the opening images of THREE steps the shopper
    // cannot see, at full width, in the same window the builder is still trying to
    // hydrate in. Measured on the Ryder bundle that was 4 of the page's 6 eager
    // builder images, all for hidden panels.
    //
    // Dropping the second image costs nothing either: an in-viewport `lazy` image
    // is fetched immediately, so the visible gallery still loads exactly as it did
    // — `lazy` only defers what is genuinely off screen, which the second cell of
    // a 2-column grid on a 390px viewport always is.
    const eagerIndex = this.hasAttribute('hidden') ? -1 : 0;
    grid.innerHTML = images
      .map(
        (src, i) =>
          `<div class="bb-step__gallery-cell${single ? ' bb-step__gallery-cell--full' : ''}" on:click="#${zoomId}/open/${i}">` +
          `<img src="${this.#esc(src)}" alt="${this.#esc(product.title)} ${i + 1}" width="800" height="800" loading="${i === eagerIndex ? 'eager' : 'lazy'}">` +
          `</div>`
      )
      .join('');

    this.#renderZoom(product, images);
  }

  /** Id the gallery cells point their `open` binding at. */
  get #zoomId() {
    return `bb-zoom-${this.stepIndex}`;
  }

  /**
   * Ask the CDN for a bigger render of an image the grid only needed at 800px.
   * `zoom-dialog` swaps to `data_max_resolution` once a slide is in view, so the
   * modal opens on the image already in cache and sharpens in place.
   * @param {string} src
   */
  #maxResolution(src) {
    const url = String(src ?? '');
    // Only rewrite a width we recognise; anything else is served back untouched
    // rather than guessed at.
    return /([?&]width=)\d+/.test(url) ? url.replace(/([?&]width=)\d+/, '$12048') : url;
  }

  /**
   * Build the zoom modal for the selected product.
   *
   * Mirrors the markup `blocks/_product-media-gallery.liquid` renders for the
   * PDP, because `zoom-dialog` reads that structure directly: `ref="dialog"`,
   * `ref="thumbnails"` and one `ref="media[]"` per slide, with `open`/`close`/
   * `handleThumbnailClick` bound through the framework's `on:` attributes. The
   * element is replaced wholesale per product rather than refilled, so each
   * build reconnects with its own refs and its own high-res cache.
   *
   * @param {any} product
   * @param {string[]} images
   */
  #renderZoom(product, images) {
    const host = this.refs.zoomHost;
    if (!(host instanceof HTMLElement)) return;

    // `zoom-dialog` requires a `media` ref; with no images there is nothing to
    // zoom, so leave the host empty and the cells' bindings resolve to nothing.
    if (!images.length) {
      host.innerHTML = '';
      return;
    }

    const title = this.#esc(product.title ?? '');
    const closeIcon = this.refs.zoomCloseIcon?.innerHTML ?? '';

    const thumbnails = images
      .map(
        (src, i) =>
          `<button type="button" class="button button-unstyled dialog-thumbnails-list__thumbnail"` +
          ` aria-label="${title} ${i + 1}"` +
          ` on:click="/handleThumbnailClick/${i}" on:pointerenter="/handleThumbnailPointerEnter/${i}"` +
          ` style="--aspect-ratio: 1; --gallery-aspect-ratio: 1;"${i === 0 ? ' aria-selected="true"' : ''}>` +
          `<img src="${this.#esc(src)}" alt="${title} ${i + 1}" width="120" height="120" loading="lazy">` +
          `</button>`
      )
      .join('');

    const slides = images
      .map(
        (src, i) =>
          `<li class="product-media-container product-media-container--image product-media-container--zoomable"` +
          ` ref="media[]" on:click="/close">` +
          `<drag-zoom-wrapper class="product-media__drag-zoom-wrapper">` +
          `<div class="product-media">` +
          // `ref="image"` is drag-zoom-wrapper's required ref — without it the
          // component throws on connect and the slide never becomes draggable.
          `<img class="product-media__image" ref="image" src="${this.#esc(src)}"` +
          ` data_max_resolution="${this.#esc(this.#maxResolution(src))}"` +
          ` alt="${title} ${i + 1}" width="800" height="800" loading="lazy">` +
          `</div>` +
          `</drag-zoom-wrapper>` +
          `</li>`
      )
      .join('');

    // `--thumbnail-width` and `--media-radius` are set by the PDP's media-gallery
    // block, not by base.css. Without them `width: var(--thumbnail-width)` on
    // .dialog-thumbnails-list__thumbnail is invalid, the button falls back to
    // auto and each thumbnail renders at its image's intrinsic size. Carry the
    // block's own defaults so the strip matches the PDP.
    host.innerHTML =
      `<zoom-dialog id="${this.#zoomId}" style="--thumbnail-width: 120px; --media-radius: 4px;">` +
      `<dialog ref="dialog" on:keydown="/handleKeyDown" scroll-lock>` +
      `<button type="button" class="button button-unstyled close-button dialog-zoomed-gallery__close-button"` +
      ` aria-label="Close" on:click="/close">${closeIcon}</button>` +
      `<div class="dialog-thumbnails-list-container">` +
      `<scroll-hint class="dialog-thumbnails-list list-unstyled" ref="thumbnails">` +
      `${images.length > 1 ? thumbnails : ''}` +
      `</scroll-hint>` +
      `</div>` +
      `<ul class="dialog-zoomed-gallery list-unstyled">${slides}</ul>` +
      `</dialog>` +
      `</zoom-dialog>`;
  }

  /** @param {string} v */
  #esc(v) {
    return String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  }
}

if (!customElements.get('bundle-step')) {
  customElements.define('bundle-step', BundleStepComponent);
}
